import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import { Building } from '@/reducers/types'
import { useAppDispatch } from '@/store/hooks'

import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import { fetchStaff } from '../../../reducers/maintenanceStaffReducer'
import { fetchParkingStaffUsers } from '../../../reducers/Organisation/parkingStaffReducer'
import { fetchPropertyManagersByBuildingId } from '../../../reducers/Organisation/propertyManagersReducer'
import { fetchShiftManagers } from '../../../reducers/Organisation/shiftManagersReducer'
import { fetchValetUsers } from '../../../reducers/Organisation/valetReducer'
import Dashboard from '../../Dashboard'
import ManagementUserForm from './ManagementUserForm'
import ManagementUsersList from './ManagementUsersList'
import ManagementUsersToolbar from './ManagementUsersToolbar'
import { ManagementUser, RoleOption } from './types'

const ManagementUsersPage = () => {
  const { t } = useTranslation(['management-users'])
  const { buildingId } = useParams<{ buildingId?: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const selectedBuilding = useSelector(selectActiveBuilding) as Building

  const [selectedManagementUser, setSelectedManagementUser] = useState<ManagementUser | null>(null)
  const [managementUsersLoading, setManagementUsersLoading] = useState<boolean>(false)
  const [selectedFilterRoles, setSelectedFilterRoles] = useState<RoleOption[]>([])

  const fetchManagementUsersList = async () => {
    setManagementUsersLoading(true)
    await dispatch(fetchShiftManagers(buildingId))
    await dispatch(fetchPropertyManagersByBuildingId(buildingId))
    await dispatch(fetchStaff(buildingId))
    await dispatch(fetchValetUsers(buildingId))
    await dispatch(fetchParkingStaffUsers(buildingId))
    setManagementUsersLoading(false)
  }

  useEffect(() => {
    if (buildingId) {
      fetchManagementUsersList()
    }
  }, [buildingId])

  useEffect(() => {
    if (selectedBuilding) {
      navigate('/dashboard/building-dashboard/management-users/' + selectedBuilding.id + '/new')
    }
  }, [selectedBuilding])

  return (
    <Dashboard.Container className="ctp-management-users">
      <ManagementUsersToolbar
        selectedBuilding={selectedBuilding ?? undefined}
        setFilterManagementUsersList={setSelectedFilterRoles}
      />
      <div className="ctp-management-users__inner">
        <div className="columns">
          <div className="column">
            {buildingId ? (
              <ManagementUsersList
                managementUsersLoading={managementUsersLoading}
                selectedFilterRoles={selectedFilterRoles}
                selectManagementUser={setSelectedManagementUser}
              />
            ) : (
              t('management-users:page.noBuildingSelectedMessage')
            )}
          </div>

          {buildingId && (
            <div className="column">
              <ManagementUserForm
                key={selectedManagementUser?.id}
                clearSelectedManagementUser={() => setSelectedManagementUser(null)}
                selectedManagementUser={selectedManagementUser}
              />
            </div>
          )}
        </div>
      </div>
    </Dashboard.Container>
  )
}

export default ManagementUsersPage
